import { useEffect, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { Text, Area, Toggle, Modal, ImagesEditor } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore } from "lucide-react";

const empty = { name: "", description: "", images: [], hidden: false, track_material: false, width_in: "", on_hand_in: 0 };

const toYdIn = (inches) => {
  const n = Number(inches) || 0;
  return { yd: Math.floor(n / 36), inch: Math.round((n - Math.floor(n / 36) * 36) * 100) / 100 };
};

export default function AdminFabrics() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [adj, setAdj] = useState({ yd: "", inch: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/fabrics").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        width_in: form.width_in === "" || form.width_in === null ? null : Number(form.width_in),
        on_hand_in: Number(form.on_hand_in) || 0,
      };
      if (editId) await api.put(`/admin/fabrics/${editId}`, payload);
      else await api.post("/admin/fabrics", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (f) => { await api.patch(`/admin/fabrics/${f.id}/archive?value=${!f.archived}`); load(); };

  const openEdit = (f) => { setForm({ ...empty, ...f, width_in: f.width_in ?? "", on_hand_in: f.on_hand_in ?? 0 }); setEditId(f.id); setAdj({ yd: "", inch: "" }); setOpen(true); };

  const applyAdjust = async (sign) => {
    const delta = ((Number(adj.yd) || 0) * 36 + (Number(adj.inch) || 0)) * sign;
    if (!delta) { toast.error("Enter an amount to adjust"); return; }
    try {
      const { data } = await api.post(`/admin/fabrics/${editId}/adjust`, { delta_in: delta, reason: sign > 0 ? "purchase" : "manual use" });
      setForm((f) => ({ ...f, on_hand_in: data.on_hand_in }));
      setAdj({ yd: "", inch: "" });
      toast.success(sign > 0 ? "Stock added" : "Stock removed"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // on-hand yards/inches edit (absolute set)
  const onHand = toYdIn(form.on_hand_in);
  const setOnHandYd = (v) => set("on_hand_in", (Number(v) || 0) * 36 + onHand.inch);
  const setOnHandIn = (v) => set("on_hand_in", onHand.yd * 36 + (Number(v) || 0));

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-kys-ink">Fabrics</h1><p className="text-sm text-kys-muted">Availability is set per design under Design ↔ Fabric. Turn on material tracking to limit custom orders to the fabric you have.</p></div>
        <button data-testid="new-fabric-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="flex items-center gap-1.5 rounded-full bg-kys-ink px-4 py-2 text-sm font-semibold text-white hover:bg-kys-accent"><Plus className="h-4 w-4" /> New</button>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((f) => {
          const avail = Math.round(((f.on_hand_in || 0) - (f.reserved_in || 0)) * 100) / 100;
          return (
            <div key={f.id} data-testid={`fabric-card-${f.id}`} className={`overflow-hidden rounded-lg border border-kys-blush-border bg-white ${f.archived ? "opacity-50" : ""}`}>
              <div className="aspect-square bg-kys-blush">{f.images?.[0] ? <img src={fileUrl(f.images[0])} alt={f.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center px-2 text-center text-xs text-kys-muted">No photo</div>}</div>
              <div className="p-2.5">
                <p className="truncate text-sm font-medium text-kys-ink">{f.name}</p>
                {f.track_material ? (
                  <div data-testid={`fabric-stock-${f.id}`} className="mt-1 text-[11px] leading-tight text-kys-charcoal">
                    <span className={avail <= 0 ? "font-semibold text-red-600" : "font-semibold text-green-700"}>{avail}" available</span>
                    <span className="block text-kys-muted">{f.on_hand_in || 0}" on hand · {f.reserved_in || 0}" held</span>
                  </div>
                ) : <span className="text-[10px] text-kys-muted">not tracked</span>}
                {f.hidden && <span className="ml-1 text-[10px] text-kys-muted">hidden</span>}
                <div className="mt-1.5 flex gap-2">
                  <button data-testid={`edit-fabric-${f.id}`} onClick={() => openEdit(f)} className="text-kys-muted hover:text-kys-accent"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => archive(f)} className="text-kys-muted hover:text-kys-accent">{f.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Fabric" : "New Fabric"}>
        <div className="space-y-4">
          <Text label="Fabric name" value={form.name} onChange={(v) => set("name", v)} testid="ff-name" />
          <Area label="Description" value={form.description} onChange={(v) => set("description", v)} rows={2} />
          <ImagesEditor value={form.images} onChange={(v) => set("images", v)} />
          <Toggle label="Hidden from customers" checked={form.hidden} onChange={(v) => set("hidden", v)} />

          <div className="rounded border border-kys-blush-border p-3">
            <Toggle label="Track material inventory" checked={form.track_material} onChange={(v) => set("track_material", v)} testid="ff-track" />
            {form.track_material && (
              <div className="mt-3 space-y-3">
                <Text label="Bolt / usable width (inches)" type="number" value={form.width_in} onChange={(v) => set("width_in", v)} testid="ff-width" hint="Used by the cutting calculator in Style Library." />
                <div>
                  <p className="mb-1 text-xs font-medium text-kys-charcoal">On hand (enter yards &amp; inches)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Text label="Yards" type="number" value={onHand.yd} onChange={setOnHandYd} testid="ff-onhand-yd" />
                    <Text label="Inches" type="number" value={onHand.inch} onChange={setOnHandIn} testid="ff-onhand-in" />
                  </div>
                  <p className="mt-1 text-[11px] text-kys-muted">= {Number(form.on_hand_in) || 0}" total on hand</p>
                </div>
                {editId && (
                  <div className="rounded bg-kys-blush p-3">
                    <p className="mb-1 text-xs font-medium text-kys-ink">Quick adjust (saves immediately)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Text label="Yards" type="number" value={adj.yd} onChange={(v) => setAdj((a) => ({ ...a, yd: v }))} testid="ff-adj-yd" />
                      <Text label="Inches" type="number" value={adj.inch} onChange={(v) => setAdj((a) => ({ ...a, inch: v }))} testid="ff-adj-in" />
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button data-testid="ff-adj-add" onClick={() => applyAdjust(1)} className="flex-1 rounded-full bg-green-600 py-2 text-xs font-semibold text-white hover:bg-green-700">+ Add (bought)</button>
                      <button data-testid="ff-adj-remove" onClick={() => applyAdjust(-1)} className="flex-1 rounded-full bg-kys-ink py-2 text-xs font-semibold text-white hover:bg-kys-accent">− Remove (used)</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button data-testid="save-fabric" onClick={save} className="w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save Fabric</button>
        </div>
      </Modal>
    </div>
  );
}
